import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const articles = [
  {
    id: '1',
    title: '5 Common Mistakes Businesses Make When Adopting AI',
    date: today,
    image: 'https://framerusercontent.com/images/cP1GNLLWaB10MRp9jKnjWjsZA.jpg?width=669&height=450',
  },
  {
    id: '2',
    title: 'How to Spot AI Automation Opportunities in Your Workflow',
    date: today,
    image: 'https://framerusercontent.com/images/8sYyKjze9DvubaHdtzGBklijw.jpg?width=668&height=687',
  },
  {
    id: '3',
    title: 'How a Small Retail Business Saved 50+ Hours per Month with AI',
    date: today,
    image: 'https://framerusercontent.com/images/03R76oStnhKNMY4bxeygKssJa30.jpg?width=666&height=933',
  },
  {
    id: '4',
    title: 'Top 10 AI Tools Every Business Should Consider',
    date: today,
    image: 'https://framerusercontent.com/images/cP1GNLLWaB10MRp9jKnjWjsZA.jpg?width=669&height=450',
  },
];

/* ── Typewriter card ── */
function TypewriterCard({ a }) {
  const [displayed, setDisplayed] = useState(a.title);
  const [typing, setTyping] = useState(false);
  const timerRef = useRef(null);
  const indexRef = useRef(a.title.length);

  const startType = useCallback(() => {
    clearTimeout(timerRef.current);
    setTyping(true);
    setDisplayed('');
    indexRef.current = 0;
    const type = () => {
      indexRef.current += 1;
      setDisplayed(a.title.slice(0, indexRef.current));
      if (indexRef.current < a.title.length) {
        timerRef.current = setTimeout(type, 26);
      } else {
        setTyping(false);
      }
    };
    timerRef.current = setTimeout(type, 26);
  }, [a.title]);

  const stopType = useCallback(() => {
    clearTimeout(timerRef.current);
    setTyping(false);
    setDisplayed(a.title);
    indexRef.current = a.title.length;
  }, [a.title]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <Link
      to={`/news/${a.id}`}
      className="ns-card"
      onMouseEnter={startType}
      onMouseLeave={stopType}
    >
      <p className="ns-date">{a.date}</p>
      <div className="ns-img-wrap">
        <img src={a.image} alt={a.title} className="ns-img" loading="lazy" />
      </div>
      <h3 className="ns-title">
        {displayed}
        {typing && <span className="ns-cursor">|</span>}
      </h3>
    </Link>
  );
}

export default function NewsSection() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

        @keyframes ns-grain {
          0%  { background-position: 0 0; }
          20% { background-position: -50px -30px; }
          40% { background-position: 30px -60px; }
          60% { background-position: -70px 20px; }
          80% { background-position: 40px 50px; }
          100%{ background-position: -20px -80px; }
        }

        @keyframes ns-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }

        /* ── white noise grain — scoped to section only ── */
        .ns-section::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          opacity: 0.32;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n' color-interpolation-filters='sRGB'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3CfeBlend in='SourceGraphic' mode='screen'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E");
          background-repeat: repeat;
          background-size: 150px 150px;
          animation: ns-grain 0.4s steps(1) infinite;
          mix-blend-mode: screen;
        }

        .ns-section {
          position: relative;
          background: #0a0a0a;
          padding: 0;
          font-family: 'Bricolage Grotesque', sans-serif;
          border-top: 1px solid rgba(255,255,255,0.08);
        }

        .ns-header,
        .ns-grid-wrap { position: relative; z-index: 1; }

        /* ── header ── */
        .ns-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: clamp(40px,6vw,80px) clamp(16px,4vw,48px) clamp(24px,3vw,40px);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .ns-label {
          font-size: 0.68rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin: 0 0 10px;
          font-family: 'Bricolage Grotesque', sans-serif;
          text-align: center;
        }
        .ns-heading {
          font-family: 'Bricolage Grotesque', sans-serif;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem);
          font-weight: 800;
          color: #fff;
          margin: 0;
          letter-spacing: -0.035em;
          line-height: 1.08;
          text-align: center;
          text-transform: none;
        }

        /* ── grid ── */
        .ns-grid { display: grid; grid-template-columns: repeat(4, 1fr); }

        /* ── card ── */
        .ns-card {
          display: flex;
          flex-direction: column;
          text-decoration: none;
          color: inherit;
          border-right: 1px solid rgba(255,255,255,0.08);
          padding: clamp(16px,2.5vw,32px) clamp(16px,2.5vw,32px) clamp(24px,3vw,48px);
          box-sizing: border-box;
          transition: background 0.3s;
          overflow: hidden;
        }
        .ns-card:first-child { border-left: 1px solid rgba(255,255,255,0.08); }
        .ns-card:hover { background: rgba(255,255,255,0.02); }

        /* date */
        .ns-date {
          font-size: 0.62rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #ec2b28;
          margin: 0 0 14px;
          font-family: 'Bricolage Grotesque', sans-serif;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .ns-date::before {
          content: '';
          display: inline-block;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #ec2b28;
          flex-shrink: 0;
        }

        /* image */
        .ns-img-wrap {
          width: 100%;
          overflow: hidden;
          border-radius: 2px;
          margin-bottom: 18px;
        }
        .ns-img {
          width: 100%;
          display: block;
          object-fit: cover;
          transform: scale(1);
          transition: transform 0.55s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .ns-card:hover .ns-img { transform: scale(1.06); }

        /* title */
        .ns-title {
          font-size: clamp(0.9rem,1.3vw,1.1rem);
          font-weight: 500;
          color: #fff;
          margin: 0;
          line-height: 1.45;
          letter-spacing: -0.01em;
          min-height: 2.9em; /* prevent layout jump while typing */
        }

        /* blinking cursor */
        .ns-cursor {
          display: inline-block;
          color: #ec2b28;
          font-weight: 400;
          margin-left: 1px;
          animation: ns-blink 0.6s step-start infinite;
        }

        .ns-grid-wrap { border-bottom: 1px solid rgba(255,255,255,0.08); }

        /* tablet */
        @media (max-width: 1024px) {
          .ns-grid { grid-template-columns: repeat(2, 1fr); }
          .ns-card:nth-child(2) { border-right: none; }
          .ns-card:nth-child(odd) { border-left: 1px solid rgba(255,255,255,0.08); }
          .ns-card:nth-child(3),
          .ns-card:nth-child(4) { border-top: 1px solid rgba(255,255,255,0.08); }
        }

        /* mobile */
        @media (max-width: 600px) {
          .ns-grid { grid-template-columns: 1fr; }
          .ns-card {
            border-right: none !important;
            border-left: none !important;
            border-top: 1px solid rgba(255,255,255,0.08);
            padding: 20px 16px 28px;
          }
          .ns-card:first-child { border-top: none; }
          .ns-header { padding: 40px 16px 24px; }
        }
      `}</style>

      <section className="ns-section">
        <div className="ns-header">
          <p className="ns-label">News</p>
          <h2 className="ns-heading">Latest Articles</h2>
        </div>

        <div className="ns-grid-wrap">
          <div className="ns-grid">
            {articles.map((a) => (
              <TypewriterCard key={a.id} a={a} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}